import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormikProps } from 'formik';
import {
  ERROR_MESSAGES,
  UX_MIN_LOADING_TIME_MS,
  API_TIMEOUT_MS,
  businessInfoInitialValues,
  getValidIndustrySuggestionsFromOptions,
  sleep,
  normalizeAbn,
} from './useAbnUtils';
import {
  isApiSuccess,
  createFormError,
  MappedError,
  defaultErrorMapper,
  isAbortError,
  ApiResponse,
} from 'types/common/ApiHelpers';
import { logEvent, selectFormatter } from 'utils';
import { Selectable } from '@zip/business-components';
import {
  BusinessInfoProps,
  AISuggestionMap,
  PatchType,
} from 'types/interfaces/sign-up/MerchantApplication.types';
import { logError } from 'utils';
import { PageAction } from 'enums';
import { SignUpStepKey } from 'modules/sign-up/signUpConfig';
import { useMerchantApplicationContext } from 'contexts';
import { AbnLookUpResponse, BusinessInfoAISuggestionsResponse } from 'types';

type UseAbnLookupFlowParams = {
  formik: FormikProps<BusinessInfoProps>;
  websiteUrl: string;
  merchantApplicationId?: string;
  saveBusinessInfoDraft: (patch: BusinessInfoProps) => void;
  checkExistingApplicationOrMerchantByAbn: (abn: string) => Promise<any>;
  abnLookUp: (abn: string) => Promise<ApiResponse<AbnLookUpResponse> | null>;
  getApplicationFormOptions: () => Promise<any>;
  getMerchantBusinessInfoAISuggestionsAsync: (payload: {
    application_id: string;
    url: string;
  }) => Promise<any>;
  aiSuggestions: AISuggestionMap;
  setAISuggestionsInContext: <K extends keyof AISuggestionMap>(
    key: K,
    suggestion: AISuggestionMap[K]
  ) => void;
  onApiError?: (error: MappedError) => void;
  abortSignUpRequests?: () => void;
  abortAISuggestionRequests?: () => void;
};

export const useAbnLookupFlow = ({
  formik,
  websiteUrl,
  merchantApplicationId,
  saveBusinessInfoDraft,
  checkExistingApplicationOrMerchantByAbn,
  abnLookUp,
  getApplicationFormOptions,
  getMerchantBusinessInfoAISuggestionsAsync,
  aiSuggestions,
  setAISuggestionsInContext,
  onApiError,
  abortSignUpRequests,
  abortAISuggestionRequests,
}: UseAbnLookupFlowParams) => {
  const { setStepData, getStepData } = useMerchantApplicationContext();

  const [abnErrors, setAbnErrors] = useState<string | null>(null);
  const [sparkleState, setSparkleState] = useState({
    isLoading: false,
    message: '',
  });

  const [entityNameVisible, setEntityNameVisible] = useState(false);
  const [tradingNameVisible, setTradingNameVisible] = useState(false);
  const [industryTypeVisible, setIndustryTypeVisible] = useState(false);

  const [industryTypeOptions, setIndustryTypeOptions] = useState<Selectable[]>(
    []
  );
  const [aiSuggestedIndustryTypes, setAiSuggestedIndustryTypes] = useState<
    string[]
  >([]);
  const [aiSuggestedTradingName, setAiSuggestedTradingName] = useState('');

  const activeRequestIdRef = useRef(0);
  const hasInitializedRef = useRef(false);
  const isMountedRef = useRef(true);

  const waitBasedOnElapsed = useCallback(
    async (minDelayMs: number, startTime: number): Promise<number> => {
      const elapsed = Date.now() - startTime;

      if (elapsed >= minDelayMs) {
        return elapsed;
      }

      const waitTime = minDelayMs - elapsed;
      if (waitTime > 0) {
        await sleep(waitTime);
      }

      return Date.now() - startTime;
    },
    []
  );

  const createNextRequestId = useCallback(() => {
    activeRequestIdRef.current += 1;
    return activeRequestIdRef.current;
  }, []);

  const isStaleRequest = useCallback((requestId: number) => {
    return activeRequestIdRef.current !== requestId;
  }, []);

  const showLoadingMessage = useCallback((message: string) => {
    setSparkleState({ isLoading: true, message });
  }, []);

  const resetAbnErrors = useCallback(() => {
    setAbnErrors(null);
  }, []);

  const resetAISuggestionSections = useCallback(() => {
    setAiSuggestedTradingName('');
    setAiSuggestedIndustryTypes([]);
    setAISuggestionsInContext(SignUpStepKey.BusinessInfo, null);
  }, [setAISuggestionsInContext]);

  const resetSparkleState = useCallback(() => {
    setSparkleState({ isLoading: false, message: '' });
  }, []);

  const hideBusinessFields = useCallback(() => {
    setEntityNameVisible(false);
    setTradingNameVisible(false);
    setIndustryTypeVisible(false);
  }, []);

  const showAISuggestionSections = useCallback(() => {
    setTradingNameVisible(true);
    setIndustryTypeVisible(true);
  }, []);

  const setAISuggestionFieldErrors = useCallback(() => {
    formik.setFieldError(
      'tradingName',
      ERROR_MESSAGES.AI_SUGGESTION_NOT_FOUND('trading name')
    );
    formik.setFieldTouched('tradingName', true, false);

    formik.setFieldError(
      'industryType',
      ERROR_MESSAGES.AI_SUGGESTION_NOT_FOUND('industry type')
    );
    formik.setFieldTouched('industryType', true, false);
  }, [formik]);

  const getCachedAISuggestion = useCallback(
    () => aiSuggestions[SignUpStepKey.BusinessInfo] ?? null,
    [aiSuggestions]
  );

  const resetFormPreserveAbn = useCallback(() => {
    formik.setValues(
      (prev) => ({
        ...prev,
        entityName: '',
        entityType: '',
        tradingName: '',
        tradingSince: undefined,
        industryType: '',
      }),
      false
    );
    hideBusinessFields();
    resetAISuggestionSections();
    resetSparkleState();
  }, [
    formik,
    hideBusinessFields,
    resetAISuggestionSections,
    resetSparkleState,
  ]);

  const resetAbn = useCallback(() => {
    createNextRequestId();
    resetAbnErrors();
    resetFormPreserveAbn();
    resetAISuggestionSections();
    abortSignUpRequests?.();
    abortAISuggestionRequests?.();
    resetSparkleState();

    formik.resetForm({ values: businessInfoInitialValues });

    setStepData(
      SignUpStepKey.BusinessInfo,
      businessInfoInitialValues,
      PatchType.SignUpInfo,
      true
    );
  }, [
    createNextRequestId,
    resetAbnErrors,
    resetFormPreserveAbn,
    resetAISuggestionSections,
    abortSignUpRequests,
    abortAISuggestionRequests,
    resetSparkleState,
    formik,
    setStepData,
  ]);

  const populateAISuggestionSections = useCallback(
    (response: BusinessInfoAISuggestionsResponse, options: Selectable[]) => {
      const tradingName = response?.data?.trading_name;
      const industrySuggestions = getValidIndustrySuggestionsFromOptions(
        options,
        response?.data?.industry_type
      );

      if (tradingName) {
        setAiSuggestedTradingName(tradingName);
      }

      if (industrySuggestions.length > 0) {
        setAiSuggestedIndustryTypes(industrySuggestions);
      }
    },
    []
  );

  const loadIndustryTypeOptions = useCallback(
    async (requestId: number): Promise<Selectable[]> => {
      try {
        const startTime = Date.now();
        const response = await getApplicationFormOptions();

        const elapsed = await waitBasedOnElapsed(
          UX_MIN_LOADING_TIME_MS.INDUSTRY_TYPE,
          startTime
        );

        if (!isMountedRef.current || isStaleRequest(requestId)) {
          return [];
        }

        if (elapsed >= API_TIMEOUT_MS.INDUSTRY_TYPE) {
          throw new Error();
        }

        const industryTypeData = Object.values(response).find(
          (item: any) => item?.questionType === 'IndustryTypes'
        ) as { candidateAnswers?: string[] } | undefined;

        if (!industryTypeData?.candidateAnswers?.length) {
          onApiError?.(
            createFormError(ERROR_MESSAGES.INDUSTRY_TYPES_NOT_FOUND)
          );
          return [];
        }

        const options = selectFormatter(
          industryTypeData.candidateAnswers.map((answer: string) => ({
            industryType: answer,
            industryTypeId: answer,
          })),
          'industryType',
          'industryTypeId'
        );

        setIndustryTypeOptions(options);
        return options;
      } catch (error) {
        if (isAbortError(error)) {
          return [];
        }

        if (!isMountedRef.current || isStaleRequest(requestId)) {
          return [];
        }

        logError(PageAction.merchantOnboardingIndustryTypeLoad, error);
        throw error;
      }
    },
    [getApplicationFormOptions, isStaleRequest, onApiError, waitBasedOnElapsed]
  );

  const callAISuggestionAPI =
    useCallback(async (): Promise<BusinessInfoAISuggestionsResponse | null> => {
      const startTime = Date.now();
      const response = await getMerchantBusinessInfoAISuggestionsAsync({
        application_id: merchantApplicationId,
        url: websiteUrl,
      });

      if (!response) {
        return null;
      }

      const elapsed = await waitBasedOnElapsed(
        UX_MIN_LOADING_TIME_MS.TRADING_NAME,
        startTime
      );

      if (elapsed >= API_TIMEOUT_MS.TRADING_NAME) {
        return null;
      }

      return response;
    }, [
      getMerchantBusinessInfoAISuggestionsAsync,
      merchantApplicationId,
      websiteUrl,
      waitBasedOnElapsed,
    ]);

  const getAISuggestions = useCallback(
    async (options: Selectable[], requestId: number) => {
      abortAISuggestionRequests?.();

      if (!websiteUrl || !merchantApplicationId) {
        resetAISuggestionSections();
        showAISuggestionSections();
        return;
      }

      const cachedAI = getCachedAISuggestion();

      if (cachedAI?.websiteUrl === websiteUrl) {
        if (!isMountedRef.current || isStaleRequest(requestId)) {
          return;
        }

        populateAISuggestionSections(
          cachedAI.response as BusinessInfoAISuggestionsResponse,
          options
        );
        showAISuggestionSections();
        return;
      }

      try {
        showLoadingMessage('Searching for your trading name...');

        const response = await callAISuggestionAPI();

        if (!isMountedRef.current || isStaleRequest(requestId)) {
          return;
        }

        if (!response) {
          setAISuggestionFieldErrors();
          showAISuggestionSections();
          resetSparkleState();
          return;
        }

        populateAISuggestionSections(response, options);
        setTradingNameVisible(true);

        showLoadingMessage('Searching for your industry type...');

        await sleep(UX_MIN_LOADING_TIME_MS.INDUSTRY_TYPE);

        if (!isMountedRef.current || isStaleRequest(requestId)) {
          return;
        }

        setIndustryTypeVisible(true);

        setAISuggestionsInContext(SignUpStepKey.BusinessInfo, {
          response,
          websiteUrl,
        });
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        if (!isMountedRef.current || isStaleRequest(requestId)) {
          return;
        }

        setAISuggestionFieldErrors();
        showAISuggestionSections();
      } finally {
        if (!isMountedRef.current || isStaleRequest(requestId)) {
          return;
        }

        resetSparkleState();
      }
    },
    [
      abortAISuggestionRequests,
      websiteUrl,
      merchantApplicationId,
      getCachedAISuggestion,
      populateAISuggestionSections,
      callAISuggestionAPI,
      formik,
      resetAISuggestionSections,
      showAISuggestionSections,
      resetSparkleState,
      setAISuggestionsInContext,
      isStaleRequest,
      setAISuggestionFieldErrors,
    ]
  );

  const performAbnLookup = useCallback(
    async (cleanAbn: string, requestId: number) => {
      try {
        logEvent(PageAction.merchantOnboardingUAABNStatus, {
          merchantApplicationId,
        });
        showLoadingMessage('Searching for your entity name...');

        const startTime = Date.now();
        const response = await abnLookUp(cleanAbn);

        const elapsed = await waitBasedOnElapsed(
          UX_MIN_LOADING_TIME_MS.ENTITY_NAME,
          startTime
        );

        if (!isMountedRef.current || isStaleRequest(requestId)) {
          return;
        }

        if (elapsed >= API_TIMEOUT_MS.ENTITY_NAME) {
          throw new Error(ERROR_MESSAGES.ABN_INVALID);
        }

        if (!isApiSuccess(response)) {
          setAbnErrors(ERROR_MESSAGES.ABN_INVALID);
          resetFormPreserveAbn();
          return;
        }

        if (response?.status && response.status !== 'Active') {
          setAbnErrors(ERROR_MESSAGES.ABN_INVALID);
          resetFormPreserveAbn();
          return;
        }

        const lookedUpValues: BusinessInfoProps = {
          abn: cleanAbn,
          entityName: response?.companyName || response?.legalName || '',
          tradingName: '',
          entityType: response?.businessType || '',
          tradingSince: response?.tradingSince || '',
          industryType: '',
        };

        formik.setValues(lookedUpValues, false);
        saveBusinessInfoDraft(lookedUpValues);
        setEntityNameVisible(true);

        const options = await loadIndustryTypeOptions(requestId);

        if (!isMountedRef.current || isStaleRequest(requestId)) {
          return;
        }

        await getAISuggestions(options, requestId);
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        if (!isMountedRef.current || isStaleRequest(requestId)) {
          return;
        }

        logError(PageAction.merchantOnboardingAbnLookup, error);
        resetAbn();
        onApiError?.(defaultErrorMapper(error));
      } finally {
        if (!isMountedRef.current || isStaleRequest(requestId)) {
          return;
        }

        resetSparkleState();
      }
    },
    [
      abnLookUp,
      waitBasedOnElapsed,
      isStaleRequest,
      resetFormPreserveAbn,
      formik,
      saveBusinessInfoDraft,
      loadIndustryTypeOptions,
      getAISuggestions,
      resetAbn,
      onApiError,
      showLoadingMessage,
      resetSparkleState,
    ]
  );

  const checkAbnExists = useCallback(
    async (abn: string) => {
      abortSignUpRequests?.();

      const requestId = createNextRequestId();

      try {
        showLoadingMessage('We are searching for your details...');

        const startTime = Date.now();
        const result = await checkExistingApplicationOrMerchantByAbn(abn);

        const elapsed = await waitBasedOnElapsed(
          UX_MIN_LOADING_TIME_MS.CHECK_ABN_EXISTS,
          startTime
        );

        if (!isMountedRef.current || isStaleRequest(requestId)) {
          return;
        }

        if (elapsed >= API_TIMEOUT_MS.CHECK_ABN_EXISTS) {
          setAbnErrors(ERROR_MESSAGES.ABN_INVALID);
          return;
        }

        if (isApiSuccess(result) && result.existingMerchantOrApplication) {
          setAbnErrors(ERROR_MESSAGES.ABN_ALREADY_EXISTS);
          resetFormPreserveAbn();
          return;
        }

        await performAbnLookup(abn, requestId);
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        if (!isMountedRef.current || isStaleRequest(requestId)) {
          return;
        }

        logError(PageAction.merchantOnboardingAbnCheck, error);
        resetFormPreserveAbn();
        onApiError?.(defaultErrorMapper(error));
      } finally {
        if (!isMountedRef.current || isStaleRequest(requestId)) {
          return;
        }

        resetSparkleState();
      }
    },
    [
      abortSignUpRequests,
      createNextRequestId,
      checkExistingApplicationOrMerchantByAbn,
      waitBasedOnElapsed,
      isStaleRequest,
      resetFormPreserveAbn,
      performAbnLookup,
      onApiError,
      showLoadingMessage,
      resetSparkleState,
    ]
  );

  const runAbnLookupIfNeeded = useCallback(
    async (cleanAbn: string) => {
      await checkAbnExists(cleanAbn);
    },
    [checkAbnExists]
  );

  const initializeBusinessInfo = useCallback(
    async ({
      businessInfoData,
      currentWebsiteUrl,
    }: {
      businessInfoData: Partial<BusinessInfoProps>;
      currentWebsiteUrl: string;
    }) => {
      const requestId = createNextRequestId();

      try {
        const { industryType } = businessInfoData;

        showLoadingMessage('We are searching for your details...');
        setEntityNameVisible(true);

        const options = await loadIndustryTypeOptions(requestId);

        if (!isMountedRef.current || isStaleRequest(requestId)) {
          return;
        }

        if (industryType && options.length > 0) {
          const matchedOption = options.find(
            (opt) => opt.value === industryType
          );

          if (matchedOption) {
            formik.setFieldValue('industryType', matchedOption.value, false);
          }
        }

        const cachedAI = getCachedAISuggestion();
        const hasValidCachedAI =
          cachedAI && cachedAI.websiteUrl === currentWebsiteUrl;

        if (hasValidCachedAI) {
          populateAISuggestionSections(
            cachedAI.response as BusinessInfoAISuggestionsResponse,
            options
          );
        } else if (currentWebsiteUrl && merchantApplicationId) {
          abortAISuggestionRequests?.();
          const response = await callAISuggestionAPI();

          if (!isMountedRef.current || isStaleRequest(requestId)) {
            return;
          }

          populateAISuggestionSections(response, options);

          const nextValues: BusinessInfoProps = {
            ...formik.values,
            tradingName: '',
            industryType: '',
          };

          formik.setValues(nextValues, false);
          saveBusinessInfoDraft(nextValues);

          setAISuggestionsInContext(SignUpStepKey.BusinessInfo, {
            response,
            websiteUrl: currentWebsiteUrl,
          });
        }

        setTradingNameVisible(true);
        setIndustryTypeVisible(true);
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        if (!isMountedRef.current || isStaleRequest(requestId)) {
          return;
        }

        logError(PageAction.merchantOnboardingAbnInit, error);
        onApiError?.(defaultErrorMapper(error));
      } finally {
        if (!isMountedRef.current || isStaleRequest(requestId)) {
          return;
        }

        resetSparkleState();
      }
    },
    [
      createNextRequestId,
      showLoadingMessage,
      loadIndustryTypeOptions,
      isStaleRequest,
      formik,
      getCachedAISuggestion,
      merchantApplicationId,
      callAISuggestionAPI,
      populateAISuggestionSections,
      saveBusinessInfoDraft,
      setAISuggestionsInContext,
      resetSparkleState,
      onApiError,
      abortAISuggestionRequests,
    ]
  );

  useEffect(() => {
    isMountedRef.current = true;

    if (hasInitializedRef.current) {
      return () => {
        isMountedRef.current = false;
      };
    }

    hasInitializedRef.current = true;

    const businessInfoData = getStepData(
      SignUpStepKey.BusinessInfo
    ) as Partial<BusinessInfoProps>;

    const {
      entityName,
      tradingName,
      industryType,
      abn: contextAbn = '',
    } = businessInfoData ?? {};

    const hasBusinessInfo = Boolean(entityName || tradingName || industryType);
    const formAbn = formik.values.abn;

    const abnMatches =
      contextAbn === formAbn ||
      normalizeAbn(contextAbn) === normalizeAbn(formAbn);

    if (hasBusinessInfo && abnMatches) {
      void initializeBusinessInfo({
        businessInfoData,
        currentWebsiteUrl: websiteUrl,
      });
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [getStepData, formik.values.abn, websiteUrl, initializeBusinessInfo]);

  return {
    abnErrors,
    resetAbnErrors,
    sparkleState,
    entityNameVisible,
    tradingNameVisible,
    industryTypeVisible,
    industryTypeOptions,
    aiSuggestedIndustryTypes,
    aiSuggestedTradingName,
    resetFormPreserveAbn,
    resetAbn,
    runAbnLookupIfNeeded,
    loadIndustryTypeOptions,
  };
};
